import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';

// STEP KAKAO-BIZM-P2 · 승인 템플릿 다중 선택 REAL 발송 검증 (관리자 전용).
// P1 (댓글주문 live_moa1) 화면·안전장치·백엔드는 그대로 REUSE. 하단에 승인된
// LM_* 템플릿을 dropdown 으로 선택하여 각 템플릿의 변수만 입력 → 1건 실발송.
// 미리보기(dry_run)는 실제 발송 없이 본문·버튼 URL 치환 결과만 확인한다.

const StatusPill = ({ ok, label }) => (
  <span data-testid={`bizm-status-${label}`}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black ${
          ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
    <span className={`w-1.5 h-1.5 rounded-full ${ok ? 'bg-green-500' : 'bg-red-500'}`} />
    {label}: {ok ? '연결됨' : '미설정'}
  </span>
);

const COMMENT_ORDER_VARS = ['buyer_name', 'product_name', 'quantity', 'total', 'seller_cart_id'];

export default function AdminKakaoBizmTest() {
  const [status, setStatus] = useState(null);
  const [catalog, setCatalog] = useState(null);        // {templates:[], rejected_excluded:[], ...}
  const [selected, setSelected] = useState('live_moa1'); // 기본 = 기존 댓글주문
  const [form, setForm] = useState({
    receiver_phone: '',
    // comment_order 변수
    buyer_name: '', product_name: '', quantity: '', total: '', seller_cart_id: '',
    // LM_* 변수 (동적)
    variables: {},
  });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    api.get('/admin/kakao-bizm/status').then((r) => setStatus(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || '상태 조회 실패'));
    api.get('/admin/kakao-bizm/templates').then((r) => setCatalog(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || '템플릿 목록 조회 실패'));
  }, []);

  const ready = !!status?.ready;

  // 현재 선택된 템플릿 메타 (dropdown 옵션 · LM_* 는 catalog.templates, live_moa1 은 특수케이스)
  const currentTemplate = useMemo(() => {
    if (selected === 'live_moa1') {
      return {
        template_code: 'live_moa1',
        label: '댓글주문 (live_moa1)',
        event: 'comment_order_confirmed',
        required_vars: COMMENT_ORDER_VARS,
        optional_vars: [],
        endpoint: 'legacy_p1',
      };
    }
    const t = (catalog?.templates || []).find((x) => x.template_code === selected);
    return t ? { ...t, endpoint: 'multi_p2' } : null;
  }, [selected, catalog]);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const patchVar = (k, v) => setForm((f) => ({ ...f, variables: { ...(f.variables || {}), [k]: v } }));

  const allVarsFilled = useMemo(() => {
    if (!currentTemplate) return false;
    if (!String(form.receiver_phone || '').trim()) return false;
    if (currentTemplate.endpoint === 'legacy_p1') {
      return COMMENT_ORDER_VARS.every((k) => String(form[k] ?? '').trim());
    }
    return (currentTemplate.required_vars || [])
      .every((k) => String((form.variables || {})[k] ?? '').trim());
  }, [form, currentTemplate]);

  const preview = async () => {
    if (sending || currentTemplate?.endpoint !== 'multi_p2') return;
    setSending(true);
    setResult(null);
    try {
      const r = await api.post('/admin/kakao-bizm/test-send-multi', {
        template_code: currentTemplate.template_code,
        receiver_phone: form.receiver_phone,
        variables: form.variables || {},
        dry_run: true,
      });
      setResult({ ok: true, data: r.data });
      toast.success('미리보기 생성 · 실제 발송 없음');
    } catch (e) {
      const d = e?.response?.data?.detail;
      setResult({ ok: false, error: d || e.message });
      toast.error(typeof d === 'string' ? d : (d?.message || d?.code || '미리보기 실패'));
    } finally {
      setSending(false);
    }
  };

  const submit = async () => {
    if (sending || !currentTemplate) return;
    if (!confirmed) {
      toast.error('실발송 동의 체크박스를 확인해주세요.');
      return;
    }
    setSending(true);
    setResult(null);
    try {
      let r;
      if (currentTemplate.endpoint === 'legacy_p1') {
        // 기존 P1 endpoint · regression 방지
        r = await api.post('/admin/kakao-bizm/test-send', {
          receiver_phone: form.receiver_phone,
          buyer_name:     form.buyer_name,
          product_name:   form.product_name,
          quantity:       Number(form.quantity),
          total:          Number(form.total),
          seller_cart_id: form.seller_cart_id,
        });
      } else {
        // P2 multi-template endpoint
        r = await api.post('/admin/kakao-bizm/test-send-multi', {
          template_code:  currentTemplate.template_code,
          receiver_phone: form.receiver_phone,
          variables:      form.variables || {},
        });
      }
      setResult({ ok: true, data: r.data });
      if (r.data?.code === 'SUCCESS') {
        toast.success(`BizM 실발송 성공 · ${r.data?.recipient_masked}`);
      } else if (r.data?.code === 'DUPLICATE_REQUEST') {
        toast.info('이미 처리된 요청 (중복 발송 차단됨)');
      } else {
        toast.error(`발송 실패: ${r.data?.bizm_result_message || r.data?.code}`);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      setResult({ ok: false, error: d || e.message });
      toast.error(typeof d === 'string' ? d : (d?.code || '요청 실패'));
    } finally {
      setSending(false);
    }
  };

  const templateOptions = useMemo(() => {
    const opts = [{ value: 'live_moa1', label: '댓글주문 · live_moa1 (P1)' }];
    (catalog?.templates || []).forEach((t) => {
      opts.push({ value: t.template_code, label: `${t.label} · ${t.template_code}` });
    });
    return opts;
  }, [catalog]);

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-4" data-testid="admin-bizm-test-page">
      <div className="rounded-xl border border-black/10 dark:border-white/10 p-4 bg-white dark:bg-[#0A0A0A]">
        <h1 className="text-lg font-black mb-1">BizM 실발송 테스트 (관리자 전용)</h1>
        <p className="text-xs text-gray-500 mb-3">
          KAKAO-BIZM-P1 (댓글주문) · P2 (승인 템플릿 다중) · 단일 수신번호 1건 실발송 검증.
          팔로워 대량 발송 · Wallet 차감 · 자동 스케줄 = 전부 비활성.
        </p>
        <div className="flex flex-wrap gap-2">
          <StatusPill ok={!!status?.status?.userid_configured} label="userid" />
          <StatusPill ok={!!status?.status?.sender_key_configured} label="sender_key" />
          <StatusPill ok={!!status?.status?.template_code_configured} label="template" />
        </div>
        {!ready && (
          <p className="mt-2 text-[11px] text-red-700" data-testid="bizm-not-ready-warning">
            Emergent Secrets 에서 BIZM_USER_ID · BIZM_SENDER_KEY · BIZM_BROADCAST_TEMPLATE_CODE 를 설정한 뒤 재배포하세요.
          </p>
        )}
        {catalog?.rejected_excluded?.length > 0 && (
          <p className="mt-2 text-[11px] text-amber-800" data-testid="bizm-rejected-note">
            반려 상태로 제외된 템플릿: {catalog.rejected_excluded.join(', ')} · 문구 수정/재검수는 별도 STEP.
          </p>
        )}
      </div>

      <div className="rounded-xl border border-black/10 dark:border-white/10 p-4 bg-white dark:bg-[#0A0A0A] space-y-3">
        <label className="block">
          <span className="text-[11px] font-bold text-gray-700 dark:text-gray-200">테스트 템플릿 선택</span>
          <select
            value={selected}
            onChange={(e) => { setSelected(e.target.value); setResult(null); }}
            className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
            data-testid="bizm-template-select">
            {templateOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-[11px] font-bold text-gray-700 dark:text-gray-200">수신번호 (010-XXXX-XXXX)</span>
          <input
            type="tel"
            value={form.receiver_phone}
            onChange={(e) => patch('receiver_phone', e.target.value)}
            placeholder="010-1234-5678"
            className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
            data-testid="bizm-input-receiver_phone" />
        </label>

        {/* 댓글주문 (live_moa1) · 기존 P1 변수 그대로 노출 */}
        {currentTemplate?.endpoint === 'legacy_p1' && (
          <div className="space-y-2" data-testid="bizm-template-form-live_moa1">
            {[
              { k: 'buyer_name', label: 'buyer_name', ph: '홍길동', type: 'text' },
              { k: 'product_name', label: 'product_name', ph: '가을 특가 상품 A', type: 'text' },
              { k: 'quantity', label: 'quantity', ph: '2', type: 'number' },
              { k: 'total', label: 'total (원)', ph: '39000', type: 'number' },
              { k: 'seller_cart_id', label: 'seller_cart_id', ph: 'sc_abcdef123456', type: 'text' },
            ].map((f) => (
              <label key={f.k} className="block">
                <span className="text-[11px] font-bold text-gray-700 dark:text-gray-200">{f.label}</span>
                <input
                  type={f.type}
                  value={form[f.k]}
                  onChange={(e) => patch(f.k, e.target.value)}
                  placeholder={f.ph}
                  className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
                  data-testid={`bizm-input-${f.k}`}
                />
              </label>
            ))}
          </div>
        )}

        {/* LM_* 승인 템플릿 · required + optional 변수 동적 노출 */}
        {currentTemplate?.endpoint === 'multi_p2' && (
          <div className="space-y-2" data-testid={`bizm-template-form-${currentTemplate.template_code}`}>
            {(currentTemplate.required_vars || []).map((k) => (
              <label key={k} className="block">
                <span className="text-[11px] font-bold text-gray-700 dark:text-gray-200">{k} <span className="text-red-500">*</span></span>
                <input
                  type="text"
                  value={(form.variables || {})[k] || ''}
                  onChange={(e) => patchVar(k, e.target.value.trim())}
                  placeholder={k === 'order_no' ? '주문번호 예: 20261101-0630DC' : undefined}
                  className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
                  data-testid={`bizm-var-${k}`} />
              </label>
            ))}
            {(currentTemplate.optional_vars || []).map((k) => (
              <label key={k} className="block">
                <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400">{k} (선택)</span>
                <input
                  type="text"
                  value={(form.variables || {})[k] || ''}
                  onChange={(e) => patchVar(k, e.target.value)}
                  className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
                  data-testid={`bizm-var-${k}`} />
              </label>
            ))}
            {currentTemplate.has_fixed_payload && (
              <p className="text-[10px] text-gray-500" data-testid={`bizm-fixed-payload-${currentTemplate.template_code}`}>
                이 템플릿은 결정값(예: decision)이 서버에서 고정 주입됩니다. 임의 변경 불가.
              </p>
            )}
          </div>
        )}

        <div className="rounded border border-amber-300/60 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-[11px] text-amber-900 dark:text-amber-200">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5"
              data-testid="bizm-consent-checkbox" />
            <span>
              <b>실제 카카오 알림톡 1건이 발송</b>되며 <b>BizM 발송비가 발생</b>할 수 있음을 확인했습니다.
              팔로워 대량 발송이 아니며 수신번호 정확히 1개에 대해서만 발송됩니다.
            </span>
          </label>
        </div>
        {currentTemplate?.endpoint === 'multi_p2' && (
          <button
            type="button"
            disabled={!allVarsFilled || sending}
            onClick={preview}
            className="w-full py-2 rounded border border-black/20 dark:border-white/20 font-black text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="bizm-preview-btn">
            미리보기 (발송 안 함)
          </button>
        )}
        <button
          type="button"
          disabled={!ready || !allVarsFilled || !confirmed || sending || !currentTemplate}
          onClick={submit}
          className="w-full py-2 rounded bg-[#FF3B30] text-white font-black text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="bizm-send-btn">
          {sending ? '발송 중...' : '1건 테스트 발송'}
        </button>
      </div>

      {result && (
        <div className="rounded-xl border border-black/10 dark:border-white/10 p-4 bg-white dark:bg-[#0A0A0A]"
             data-testid="bizm-result-card">
          <h3 className="text-sm font-black mb-2">발송 결과</h3>
          <pre className="text-[10px] leading-relaxed whitespace-pre-wrap break-all bg-gray-50 dark:bg-black/40 p-3 rounded">
{JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
